/**
 * How the visit that ends in a sign-up started, stored per new account in
 * Firestore `profilesSignupAttribution/{uid}`. It is sent along with the sign-up to the
 * Cloud Function createUserProfileV2 (hushdesire-cloud-functions/functions-v2) as
 * `attribution: signupAttributionPayload(lang)`.
 *
 * The same file lives in gatsby-seo, bloom and bloom-ad-landingpages; keep them in sync.
 *
 * recordVisit() runs on every page view. A visit works like a PostHog / GA session: it
 * starts on a page view after 30 minutes without activity, or on any page view whose URL
 * carries UTM parameters (a new campaign click), and its first page is the landing page.
 * It lives in localStorage rather than React state so reloads and new tabs keep it, and
 * localStorage is per origin, so every site records only its own visits.
 *
 * One exception to the 30 minutes: a visit that came in through a campaign link survives
 * breaks for 24 hours after the click, unless the person arrives from another site
 * meanwhile. Listening to a story is no page view, so a podcast listener who listened for
 * 40 minutes and then signed up used to count as direct. Such a visit is marked
 * `resumedAt`, so satellite-asmr keeps its UTMs off the profile (where utm_campaign redeems
 * promo and affiliate codes). This only changes what the /signups dashboard reads.
 *
 * Only paths are stored, never query strings: URLs here can carry login tokens
 * (/login?token=…) and click ids. UTMs are read out of the landing URL on their own.
 *
 * The browser only reports what only it can know; the function takes host and sign-in
 * method from the request (its Origin) and the ID token, and checks everything else.
 */

const STORAGE_KEY = "signupVisit";
const VISIT_TIMEOUT_MS = 30 * 60 * 1000;
const CAMPAIGN_WINDOW_MS = 24 * 60 * 60 * 1000;

// The first recordVisit() of this page load: only there does document.referrer say where the
// person just came from (in-app navigation keeps the referrer of the page load).
let isFirstPageView = true;

const hasUtm = (utm) => Boolean(utm && (utm.source || utm.medium || utm.campaign));

function readVisit() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function recordVisit() {
  if (typeof window === "undefined") return;
  const firstPageView = isFirstPageView;
  isFirstPageView = false;
  try {
    const now = Date.now();
    const params = new URLSearchParams(window.location.search);
    const utm = {
      source: params.get("utm_source") || "",
      medium: params.get("utm_medium") || "",
      campaign: params.get("utm_campaign") || "",
    };
    const stored = readVisit();
    // Only where the visit came from, and only when that is another site.
    const referrerHost = document.referrer ? new URL(document.referrer).hostname : "";
    const otherSite = referrerHost && referrerHost !== window.location.hostname ? referrerHost : null;
    const timedOut = !stored || now - stored.lastSeenAt > VISIT_TIMEOUT_MS;
    const resumesCampaign =
      timedOut &&
      Boolean(stored) &&
      hasUtm(stored.utm) &&
      now - stored.startedAt < CAMPAIGN_WINDOW_MS &&
      !(firstPageView && otherSite);
    const isNewVisit = hasUtm(utm) || (timedOut && !resumesCampaign);
    const visit = isNewVisit
      ? {
          landingPath: window.location.pathname,
          referrer: otherSite,
          utm,
          startedAt: now,
          lastSeenAt: now,
        }
      : { ...stored, lastSeenAt: now, ...(resumesCampaign && { resumedAt: now }) };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visit));
  } catch {
    // Storage can be unavailable (blocked, private mode); attribution is best-effort.
  }
}

/**
 * What the browser knows about the visit that led to this sign-up, for createUserProfileV2's
 * `attribution`. Sending it with that request (which the sites await before redirecting)
 * replaced a separate beacon, which the redirect could cancel and iOS Safari sent without
 * an Origin header.
 */
export function signupAttributionPayload(lang) {
  if (typeof window === "undefined") return null;
  const visit = readVisit();
  return {
    lang: lang || null,
    landingPath: visit?.landingPath ?? null,
    referrer: visit?.referrer ?? null,
    utm: visit?.utm ?? null,
    visitStartedAt: visit?.startedAt ?? null,
    signupPath: window.location.pathname,
  };
}
